import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Home, Shield, FileText, Search, Calculator, Users, Target, Eye, MessageSquare, Award } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SectionTitle from "@/components/SectionTitle";
import ContactForm from "@/components/ContactForm";

const RealEstate = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 px-4">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 sm:mb-6 leading-tight">
            ליווי משפטי מקיף בעסקאות מקרקעין: הבטחת ההשקעה החשובה בחייכם
          </h1>
          <h2 className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-6 sm:mb-8 leading-relaxed">
            מרכישת דירת מגורים ועד ניהול עסקאות נדל"ן מורכבות – משרדנו מעניק ליווי משפטי מקצועי, יסודי ואישי להגנה מלאה על הזכויות והאינטרסים שלכם.
          </h2>
          <Link to="/contact">
            <Button size="lg" className="text-base sm:text-lg px-6 sm:px-10 py-6 sm:py-7 w-full sm:w-auto">
              לקבלת ייעוץ ראשוני ללא התחייבות
            </Button>
          </Link>
        </div>
      </section>

      {/* Opening Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-5xl">
          <p className="text-base sm:text-lg md:text-xl text-foreground leading-relaxed mb-4 sm:mb-6">
            עסקת מקרקעין היא הרבה יותר מחתימה על חוזה. היא כרוכה בבדיקות משפטיות, תכנוניות ומיסוייות מורכבות, כאשר לכל פרט קטן עלולות להיות השלכות הרות גורל. טעות אחת, סעיף שלא הובן כהלכה או בדיקה שלא בוצעה – עלולים לעלות לכם מאות אלפי שקלים ולגרום לעוגמת נפש אדירה.
          </p>
          <p className="text-base sm:text-lg md:text-xl text-foreground leading-relaxed font-medium">
            התפקיד שלנו הוא להיות שם עבורכם ובשבילכם. אנו מבינים את גודל המעמד – את ההתרגשות לצד החששות. משרדנו מביא עמו ניסיון רב-שנים בניהול כל סוגי עסקאות הנדל"ן, ומחויב לספק לכם שקט נפשי מלא, בידיעה שהאינטרסים שלכם מוגנים מן המסד ועד הטפחות.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-7xl">
          <SectionTitle 
            title="תחומי ההתמחות שלנו במקרקעין"
            subtitle="אנו מספקים מעטפת משפטית של 360 מעלות לכל צורך בתחום הנדל״ן"
            align="center"
            className="mb-10 sm:mb-16"
          />
          
          <div className="space-y-6 sm:space-y-10">
            {/* Service 1 - Buyers */}
            <Card className="hover:shadow-xl transition-all duration-300 border-2">
              <CardHeader className="pb-4 sm:pb-6">
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0 w-14 h-14 sm:w-20 sm:h-20 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Home className="w-8 h-8 sm:w-12 sm:h-12 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl sm:text-2xl md:text-3xl mb-2 sm:mb-3">
                      1. ליווי וייצוג קונים (עסקאות יד ראשונה ושנייה)
                    </CardTitle>
                    <CardDescription className="text-sm sm:text-base md:text-lg">
                      ליווי רוכש דירה הוא אומנות הדורשת יסודיות. אנו נדאג עבורכם לכל שלבי הרכישה:
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-5">
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-muted/50 p-4 sm:p-6 rounded-lg">
                    <div className="flex items-start gap-3 mb-2">
                      <Search className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-1" />
                      <h4 className="font-bold text-base sm:text-lg">בדיקות מקדמיות (Due Diligence)</h4>
                    </div>
                    <p className="text-muted-foreground text-sm sm:text-base">
                      בדיקת נסח טאבו/רמ"י/חברה משכנת, איתור עיקולים, שעבודים או הערות אזהרה סותרות.
                    </p>
                  </div>

                  <div className="bg-muted/50 p-4 sm:p-6 rounded-lg">
                    <div className="flex items-start gap-3 mb-2">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-1" />
                      <h4 className="font-bold text-base sm:text-lg">בדיקת היבטי תכנון ובנייה</h4>
                    </div>
                    <p className="text-muted-foreground text-sm sm:text-base">
                      וידוא התאמה להיתר בנייה, איתור חריגות בנייה ובדיקת זכויות בנייה עתידיות.
                    </p>
                  </div>

                  <div className="bg-muted/50 p-4 sm:p-6 rounded-lg">
                    <div className="flex items-start gap-3 mb-2">
                      <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-1" />
                      <h4 className="font-bold text-base sm:text-lg">תכנון מס</h4>
                    </div>
                    <p className="text-muted-foreground text-sm sm:text-base">
                      ניתוח היבטי מיסוי מקרקעין (מס רכישה) ובחינת זכאות לפטורים או הקלות.
                    </p>
                  </div>

                  <div className="bg-muted/50 p-4 sm:p-6 rounded-lg">
                    <div className="flex items-start gap-3 mb-2">
                      <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-1" />
                      <h4 className="font-bold text-base sm:text-lg">ניהול משא ומתן</h4>
                    </div>
                    <p className="text-muted-foreground text-sm sm:text-base">
                      ניהול מו"מ משפטי נחוש על תנאי החוזה להבטחת האינטרסים שלכם.
                    </p>
                  </div>

                  <div className="bg-muted/50 p-4 sm:p-6 rounded-lg">
                    <div className="flex items-start gap-3 mb-2">
                      <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-1" />
                      <h4 className="font-bold text-base sm:text-lg">ליווי מול הבנק</h4>
                    </div>
                    <p className="text-muted-foreground text-sm sm:text-base">
                      סיוע בהיבטים המשפטיים של נטילת משכנתא ורישום המשכנתא.
                    </p>
                  </div>

                  <div className="bg-muted/50 p-4 sm:p-6 rounded-lg">
                    <div className="flex items-start gap-3 mb-2">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-1" />
                      <h4 className="font-bold text-base sm:text-lg">רישום זכויות</h4>
                    </div>
                    <p className="text-muted-foreground text-sm sm:text-base">
                      השלמת העסקה ברישום הזכויות על שמכם בטאבו.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service 2 - Sellers */}
            <Card className="hover:shadow-xl transition-all duration-300 border-2">
              <CardHeader className="pb-4 sm:pb-6">
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0 w-14 h-14 sm:w-20 sm:h-20 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Users className="w-8 h-8 sm:w-12 sm:h-12 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl sm:text-2xl md:text-3xl mb-2 sm:mb-3">
                      2. ליווי וייצוג מוכרים
                    </CardTitle>
                    <CardDescription className="text-sm sm:text-base md:text-lg">
                      אנו מלווים אתכם בתהליך המכירה כדי להבטיח עסקה בטוחה, מהירה ורווחית:
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                  <div className="flex gap-3">
                    <span className="text-primary font-bold text-xl flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">הכנת הנכס למכירה:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">סיוע בהסדרת רישום הנכס והכנת התיק המשפטי.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-primary font-bold text-xl flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">ניסוח חוזה מכר:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">ניסוח חוזה המגן עליכם מפני הפרות ומבטיח את קבלת התמורה במלואה.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-primary font-bold text-xl flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">תכנון מס שבח:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">בחינת זכאות לפטור ממס שבח, ביצוע חישובים מדויקים וייצוג מול רשויות המס.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-primary font-bold text-xl flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">ניהול משא ומתן:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">עמידה איתנה על זכויותיכם המסחריות והמשפטיות.</p>
                    </div>
                  </div>

                  <div className="flex gap-3 sm:col-span-2">
                    <span className="text-primary font-bold text-xl flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">סילוק משכנתא/עיקולים:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">טיפול בהסרת שעבודים קיימים כחלק מהשלמת העסקה.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service 3 - Rental Agreements */}
            <Card className="hover:shadow-xl transition-all duration-300 border-2">
              <CardHeader className="pb-4 sm:pb-6">
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0 w-14 h-14 sm:w-20 sm:h-20 bg-primary/10 rounded-lg flex items-center justify-center">
                    <FileText className="w-8 h-8 sm:w-12 sm:h-12 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl sm:text-2xl md:text-3xl mb-2 sm:mb-3">
                      3. עריכת חוזי שכירות (מגורים ומסחרי)
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-muted/50 p-4 sm:p-6 rounded-lg">
                    <h4 className="font-bold text-base sm:text-lg mb-2 sm:mb-3">למשכירים:</h4>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      ניסוח חוזה המגן על הנכס שלכם, כולל מנגנוני בטוחות חזקים (ערבות בנקאית, שטר חוב וכו') וסעיפים ברורים לפינוי במקרה של הפרה.
                    </p>
                  </div>

                  <div className="bg-muted/50 p-4 sm:p-6 rounded-lg">
                    <h4 className="font-bold text-base sm:text-lg mb-2 sm:mb-3">לשוכרים:</h4>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      בדיקת החוזה, וידוא הוגנות התנאים, הגנה על זכויותיכם כשוכרים והגבלת דרישות בלתי סבירות.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service 4 - Complex Transactions */}
            <Card className="hover:shadow-xl transition-all duration-300 border-2">
              <CardHeader className="pb-4 sm:pb-6">
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0 w-14 h-14 sm:w-20 sm:h-20 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Target className="w-8 h-8 sm:w-12 sm:h-12 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl sm:text-2xl md:text-3xl mb-2 sm:mb-3">
                      4. ניהול עסקאות מורכבות ונדל"ן מסחרי
                    </CardTitle>
                    <CardDescription className="text-sm sm:text-base md:text-lg">
                      מעבר לעסקאות סטנדרטיות, משרדנו מתמחה בניהול פרויקטים ועסקאות נדל"ן מורכבות:
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {[
                    'עסקאות קומבינציה',
                    'הסכמי אופציה במקרקעין',
                    'עסקאות מתנה והעברה ללא תמורה (בין קרובים)',
                    'פירוק שיתוף במקרקעין (בהסכמה או דרך בית משפט)',
                    'רכישת ומכירת נכסים מניבים ונדל"ן מסחרי'
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-2 bg-muted/30 p-3 sm:p-4 rounded-lg">
                      <span className="text-primary font-bold flex-shrink-0">✓</span>
                      <span className="text-sm sm:text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 bg-muted/30">
        <div className="container mx-auto max-w-7xl">
          <SectionTitle 
            title="למה דווקא אנחנו?"
            subtitle="הפילוסופיה שלנו בליווי עסקאות - בעולם הנדל״ן, הניסיון הוא לא רק יתרון – הוא הכרח"
            align="center"
            className="mb-10 sm:mb-16"
          />
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <Card className="text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50">
              <CardHeader>
                <div className="flex justify-center mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full flex items-center justify-center">
                    <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-primary" />
                  </div>
                </div>
                <CardTitle className="text-lg sm:text-xl md:text-2xl mb-3 sm:mb-4">יסודיות של 100%<br />(אפס פשרות)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  אנו לא משאירים אבן על אבן. כל עסקה עוברת תחת זכוכית מגדלת, מבדיקת הרישום ההיסטורי ועד לפסיק הקטן בחוזה. אצלנו אין "יהיה בסדר" – יש רק "בטוח".
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50">
              <CardHeader>
                <div className="flex justify-center mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full flex items-center justify-center">
                    <Eye className="w-10 h-10 sm:w-12 sm:h-12 text-primary" />
                  </div>
                </div>
                <CardTitle className="text-lg sm:text-xl md:text-2xl mb-3 sm:mb-4">ראייה הוליסטית<br />(משפטית, תכנונית ומיסויית)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  עורך דין מקרקעין מעולה רואה את התמונה כולה. אנו משלבים ידע מעמיק בדיני מקרקעין, דיני תכנון ובנייה ומיסוי מקרקעין, כדי לתת לכם פתרון שלם ולמנוע הפתעות בהמשך הדרך.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50">
              <CardHeader>
                <div className="flex justify-center mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full flex items-center justify-center">
                    <Users className="w-10 h-10 sm:w-12 sm:h-12 text-primary" />
                  </div>
                </div>
                <CardTitle className="text-lg sm:text-xl md:text-2xl mb-3 sm:mb-4">זמינות וליווי אישי<br />("בוטיק")</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  אתם לא עוד לקוח במפעל. אנו מעניקים ליווי אישי וצמוד, זמינים לכל שאלה והתייעצות (ואנו יודעים שיש הרבה). אתם תמיד תדעו מי מטפל בכם ומה הסטטוס המדויק של העסקה שלכם.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50">
              <CardHeader>
                <div className="flex justify-center mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full flex items-center justify-center">
                    <Award className="w-10 h-10 sm:w-12 sm:h-12 text-primary" />
                  </div>
                </div>
                <CardTitle className="text-lg sm:text-xl md:text-2xl mb-3 sm:mb-4">מומחיות בניהול<br />משא ומתן</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  היכרותנו עם השוק ועם ניהול מאות עסקאות מאפשרת לנו לנהל משא ומתן יעיל, אסרטיבי אך הוגן, כדי להשיג עבורכם את התנאים הטובים ביותר מבלי "לפוצץ" את העסקה.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-5xl">
          <SectionTitle 
            title="שאלות נפוצות בתחום המקרקעין"
            align="center"
            className="mb-8 sm:mb-12"
          />
          
          <Accordion type="single" collapsible className="w-full space-y-4">
            <AccordionItem value="item-1" className="border-2 rounded-lg px-4 sm:px-6">
              <AccordionTrigger className="text-base sm:text-lg md:text-xl font-semibold hover:no-underline text-right py-4 sm:py-5">
                למה אני לא יכול להשתמש בעורך הדין של הצד השני (המוכר/הקבלן)?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base md:text-lg leading-relaxed pt-2 pb-4">
                זוהי טעות קריטית. עורך הדין של הצד השני מחויב אך ורק לאינטרסים של הלקוח שלו. בעסקת קבלן, עורך הדין של הקבלן מגן על הקבלן, לא עליכם. אתם חייבים עורך דין שייצג אתכם בנאמנות מוחלטת וידאג אך ורק לזכויות שלכם.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border-2 rounded-lg px-4 sm:px-6">
              <AccordionTrigger className="text-base sm:text-lg md:text-xl font-semibold hover:no-underline text-right py-4 sm:py-5">
                מהו שכר הטרחה המקובל בעסקת מכר/רכישה?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base md:text-lg leading-relaxed pt-2 pb-4">
                שכר הטרחה נגזר בדרך כלל כאחוז משווי העסקה (לרוב בין 0.5% ל-1.5% בתוספת מע"מ), או כסכום קבוע מראש, בהתאם למורכבות העסקה. אנו מאמינים בשקיפות מלאה ומסכמים את שכר הטרחה בכתב מראש.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-2 rounded-lg px-4 sm:px-6">
              <AccordionTrigger className="text-base sm:text-lg md:text-xl font-semibold hover:no-underline text-right py-4 sm:py-5">
                מהי "הערת אזהרה" ולמה היא כל כך חשובה?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base md:text-lg leading-relaxed pt-2 pb-4">
                הערת אזהרה היא רישום בטאבו המודיע לכל העולם כי התבצעה עסקה בנכס. היא מונעת מהמוכר לבצע עסקה סותרת (למכור את הנכס לאדם נוסף) ומגנה על כספכם עד להשלמת הרישום המלא. זהו אמצעי ההגנה הבסיסי והחשוב ביותר עבור הקונה.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border-2 rounded-lg px-4 sm:px-6">
              <AccordionTrigger className="text-base sm:text-lg md:text-xl font-semibold hover:no-underline text-right py-4 sm:py-5">
                מהם המיסים העיקריים בעסקת נדל"ן?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base md:text-lg leading-relaxed pt-2 pb-4">
                הקונה משלם "מס רכישה", המחושב באחוזים משווי העסקה (עם מדרגות שונות לדירה יחידה, דירה נוספת וכו'). המוכר משלם "מס שבח" על הרווח שנוצר לו מהמכירה, אלא אם הוא זכאי לפטור (למשל, במכירת דירה יחידה בתנאים מסוימים).
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
              הצעד הראשון לביטחון המשפטי שלכם בעסקת הנדל"ן
            </h2>
            <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4">
              <p className="text-base sm:text-lg md:text-xl text-foreground font-bold leading-relaxed">
                אל תחתמו על שום מסמך, אפילו לא על "זיכרון דברים", לפני שהתייעצתם.
              </p>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed">
                טעות אחת בשלב מוקדם עלולה להיות בלתי הפיכה.
              </p>
              <p className="text-base sm:text-lg md:text-xl text-foreground leading-relaxed">
                צרו קשר עוד היום לפגישת ייעוץ ראשונית, בה נבחן את פרטי העסקה, נזהה את הסיכונים וההזדמנויות, ונסביר לכם בדיוק כיצד נגן עליכם.
              </p>
            </div>
          </div>
          
          <div className="max-w-2xl mx-auto bg-background p-6 sm:p-8 md:p-10 rounded-xl shadow-lg border-2">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default RealEstate;